// deps
import { Dispatch, useReducer, useEffect, useContext, useMemo } from "react";

// internal-deps
import { ApiFetchState } from "@swiggy-private/payment-core";
import { useMountedRef, captureError } from "@swiggy-private/core-ui";
import { Ok, Result } from "@swiggy-private/js-fp";

// reducers
import {
    GetCardBalanceAction,
    defaultCardBalanceStateValue,
    CardBalanceState,
    getCardBalanceReducer,
    GetCardBalanceActionTypes,
} from "@payment-client/reducers/Card";

// apis
import { getSodexoBalance, GetSodexoBalanceResponse } from "@payment-client/apis/Card";
import { getSodexoBalancePublic } from "@payment-client/apis/publicCard";

// contexts
import { ApiContext, PublicApiContext, CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS } from "@swiggy-private/checkout-core";

// helpers
import { getAllFoodCards, getValidCardReferences } from "@payment-client/helpers/PaymentHelper";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// constants
import { CARD_API_KEYS } from "@swiggy-private/checkout-core";

function defaultReducerStateBuilder(cardReferences: string[]): CardBalanceState {
    return cardReferences.reduce<CardBalanceState>((acc, cur) => {
        acc[cur] = {
            ...defaultCardBalanceStateValue,
            fetchState: ApiFetchState.LOADING,
        };
        return acc;
    }, {});
}

const defaultBalanceResponse = {
    cardReference: "--",
    cardBalance: "--",
};

interface FetchCardBalanceResponse {
    cardReference: string;
    response: Result<GetSodexoBalanceResponse, never>;
}

async function fetchAllSodexoCardBalance(
    apiHost: string,
    isPublicApiEnabled: boolean,
): Promise<FetchCardBalanceResponse[] | null> {
    const getSodexoBalanceFn = isPublicApiEnabled ? getSodexoBalancePublic : getSodexoBalance;

    return getSodexoBalanceFn(apiHost)
        .then(response => {
            return response.map(cardResponse => ({
                cardReference: cardResponse.cardReference,
                response: Ok(cardResponse ?? defaultBalanceResponse),
            }));
        })
        .catch(err => {
            captureError(err);
            return null;
        });
}

function processFetchCardBalanceResponse(params: {
    cardResponse: FetchCardBalanceResponse[];
    cardReferences: string[];
    dispatch: Dispatch<GetCardBalanceAction>;
}): void {
    const { cardReferences, cardResponse, dispatch } = params;
    cardReferences.forEach(reference => {
        const response = cardResponse.find(card => card.cardReference === reference);
        if (!response) {
            dispatch({ name: reference, type: GetCardBalanceActionTypes.ERROR });
            return;
        }

        response.response.match({
            ok: result => {
                const payload = { ...result, balance: Number(result.cardBalance) || 0 };
                dispatch({ name: reference, type: GetCardBalanceActionTypes.SUCCESS, payload });
            },
            err: () => dispatch({ name: reference, type: GetCardBalanceActionTypes.ERROR }),
        });
    });
}

interface CardBalanceProps {
    paymentResponse: GetPaymentMethodsResponse;
    isTurboPaymentMethod: boolean;
}

export function useCardBalance(props: CardBalanceProps): CardBalanceState {
    const isMountedRef = useMountedRef();
    const { host } = useContext(ApiContext);
    const publicApiConfig = useContext(PublicApiContext);
    const paymentMethods = useMemo(() => getAllFoodCards(props.paymentResponse), [props.paymentResponse]);
    const cardReferences = useMemo(() => getValidCardReferences(paymentMethods), [paymentMethods]);
    const [reducerState, dispatch] = useReducer(getCardBalanceReducer, cardReferences, defaultReducerStateBuilder);

    const isPublicApiEnable = useMemo(
        () =>
            isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CARDS_JUSPAY_APIS,
                CARD_API_KEYS.GET_SODEXO_BALANCE,
            ),
        [publicApiConfig],
    );

    useEffect(() => {
        // We will not call sodexo-balance API for turbo checkout and instead use it from payment method meta information
        if (props.isTurboPaymentMethod || cardReferences.length === 0) {
            return;
        }

        fetchAllSodexoCardBalance(host.unwrap(), isPublicApiEnable)
            .then(cardResponse => {
                if (isMountedRef.current) {
                    cardResponse && processFetchCardBalanceResponse({ cardResponse, cardReferences, dispatch });
                }
            })
            .catch(err => {
                if (isMountedRef.current) {
                    captureError(err);
                }
            });
    }, [cardReferences, host, isMountedRef, props.isTurboPaymentMethod, isPublicApiEnable]);

    return reducerState;
}
